/**
 * Portas dos pacotes opcionais.
 *
 * A casca do aplicativo e construida ao mesmo tempo que `@flos/ai`, `@flos/auth`,
 * `@flos/sync`, `@flos/notifications` e os plugins. Em vez de a casca deixar de
 * compilar sempre que um deles ainda nao tem `index.ts`, declaramos aqui — de
 * forma estreita — apenas o que precisamos de cada um, e carregamo-los em tempo
 * de execucao (`modules.ts`).
 *
 * Isto e a regra 7 aplicada ao arranque: nada aqui sabe que existe Gemini, Apps
 * Script ou Google. Sabe que existe "um fornecedor de IA" e "um transporte".
 */
import type {
  AiAction,
  AiProvider,
  BaseRecord,
  ConflictReport,
  EventBusLike,
  IntentDescriptor,
  Logger,
  OutboxItem,
  PermissionSet,
  Scope,
  SessionInfo,
  SyncState,
  SyncStatus,
  SyncTransport,
} from '@flos/shared';

// --- @flos/ai ----------------------------------------------------------------

export interface IntentRouterLike {
  route(text: string, options?: { signal?: AbortSignal; context?: string }): Promise<AiAction>;
  setIntents(intents: IntentDescriptor[]): unknown;
  available(): IntentDescriptor[];
}

export interface AiModulePort {
  IntentRouter: new (options: {
    provider: AiProvider;
    intents?: IntentDescriptor[];
    can?: (scope: string) => boolean;
    logger?: Logger;
  }) => IntentRouterLike;
  createAiProvider: (config: { id?: string; apiKey?: string }) => AiProvider;
}

// --- @flos/auth --------------------------------------------------------------

/** O que a casca precisa de saber sobre quem esta a usar isto. */
export interface RestoredSession {
  info: SessionInfo;
  permissions?: PermissionSet;
  /**
   * ISO-8601. Ausente = sem validade conhecida.
   *
   * E a validade do token, NAO o token. Isto e gravado no `localStorage`
   * (`sessionStore.ts`) e por isso nao pode conter nada secreto — o token vive
   * so em memoria e no IndexedDB do `@flos/auth` (docs/SECURITY.md).
   */
  expiresAt?: string | null;
}

/** Identidade tal como o fornecedor de autenticacao a devolve. */
export interface AuthAccount {
  id: string;
  email: string;
  displayName: string;
  pictureUrl?: string;
}

/**
 * Sessao do fornecedor. **Contem o token** — nunca deve ser gravada por nós em
 * lado nenhum; quem a guarda e o proprio `@flos/auth`, em IndexedDB.
 */
export interface AuthProviderSession {
  user: AuthAccount;
  tokens: { idToken: string; expiresAt: number };
  familyId?: string;
  memberId: string;
  issuedAt: number;
  providerId: string;
}

/**
 * Estado da autenticacao.
 *
 * `degraded` e a razao de isto existir: token expirado nao expulsa ninguem dos
 * seus proprios dados locais (ADR-0002). Ve tudo, com o motivo escrito.
 */
export type AuthStateLike =
  | { status: 'loading' }
  | { status: 'signed_out'; reason?: string }
  | { status: 'active'; session: AuthProviderSession }
  | {
      status: 'degraded';
      session: AuthProviderSession;
      reason: 'expired_offline' | 'refresh_failed';
      message: string;
    };

/** Subconjunto do `AuthService` de que a casca depende. */
export interface AuthServiceLike {
  initialize(): Promise<AuthStateLike>;
  getState(): AuthStateLike;
  getSession(): AuthProviderSession | null;
  signIn(): Promise<AuthStateLike>;
  signOut(): Promise<AuthStateLike>;
  setFamily(familyId: string, memberId?: string): Promise<AuthStateLike>;
  getIdToken(): Promise<string | null>;
  subscribe(listener: (state: AuthStateLike) => void): () => void;
  dispose(): void;
}

/**
 * O que a casca ve da autenticacao.
 *
 * De proposito sem uma unica palavra do fornecedor: as telas sabem que ha "uma
 * conta", que ela pode estar ativa, degradada ou desligada, e que ha um botao
 * cujo texto vem daqui. Trocar de fornecedor nao toca em tela nenhuma.
 */
export interface AuthSnapshot {
  status: 'desligada' | 'a-carregar' | 'ativa' | 'degradada';
  account: AuthAccount | null;
  /**
   * Porque e que esta degradada. Decide o aviso que ela ve: sem rede nao ha
   * nada a fazer senao esperar; com rede ha um botao que resolve.
   */
  reason: 'expirada-sem-rede' | 'renovacao-falhou' | null;
  /** Frase em portugues quando ha algo a dizer-lhe. */
  message: string | null;
  /** ISO-8601 da validade do token. Nunca o token. */
  expiresAt: string | null;
}

export interface AuthControllerLike {
  /** `false` quando nao ha fornecedor configurado neste aparelho. */
  readonly available: boolean;
  /** Texto do botao de entrada, em portugues. Vem daqui, nao da tela. */
  readonly signInLabel: string;
  readonly unavailableReason: string | null;
  snapshot(): AuthSnapshot;
  /** So a partir de um gesto dela — os fornecedores exigem-no. */
  signIn(): Promise<AuthSnapshot>;
  signOut(): Promise<void>;
  /** Diz ao fornecedor em que familia ela ficou, sem novo login. */
  useFamily(familyId: string, memberId?: string): Promise<void>;
  /** Token para o transporte. `null` quando nao ha nenhum utilizavel. */
  getIdToken(): Promise<string | null>;
  /** `true` so quando ha token valido: e o que autoriza enviar para fora. */
  canSync(): boolean;
  subscribe(listener: (snapshot: AuthSnapshot) => void): () => void;
  stop(): void;
}

export interface AuthModulePort {
  createAuthService: (options: {
    provider: unknown;
    events?: EventBusLike;
    logger?: Logger;
  }) => AuthServiceLike;
  /**
   * Construtor do adaptador de identidade. O nome do fornecedor so aparece aqui
   * e em `auth.ts` — a casca so sabe que ha "uma conta" (regra 7 / ADR-0005).
   */
  GoogleAuthProvider: new (options: { clientId: string }) => unknown;
}

// --- @flos/notifications -----------------------------------------------------

export interface NotificationsModulePort {
  createNotifier: (deps: { logger?: Logger }) => {
    local(input: { title: string; body: string; at?: Date; tag?: string }): Promise<void>;
    family(input: { title: string; body: string; toRoles?: string[] }): Promise<void>;
  };
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
}

// --- @flos/sync --------------------------------------------------------------

/** Subconjunto do `SyncEngine` de que a casca depende. */
export interface SyncEngineLike {
  start(): void;
  stop(): void;
  status(): SyncStatus;
  syncNow(): Promise<SyncStatus>;
  listConflicts(): Promise<Array<ConflictReport & { id: string; detectedAt: string }>>;
  resolveConflict(id: string, resolution: ConflictResolutionChoice): Promise<unknown>;
}

export type ConflictResolutionChoice =
  | { kind: 'take_server' }
  | { kind: 'take_local' }
  | { kind: 'merge'; fields: Record<string, unknown> };

/**
 * Espelhos estruturais das portas de armazenamento de `packages/sync/src/ports.ts`.
 *
 * Sao declarados aqui, e nao importados de `@flos/sync`, pela mesma razao de
 * tudo o resto neste arquivo: `@flos/sync` e opcional e carregado em tempo de
 * execucao. Um `import type` dele faria a casca deixar de compilar no dia em
 * que o pacote nao existisse — e e exatamente isso que este arquivo evita.
 *
 * Que nao divirjam do original nao fica ao cuidado de ninguem: em
 * `dexieStores.test.ts` as implementacoes concretas sao atribuidas aos tipos
 * verdadeiros do `@flos/sync`, e o `tsc` recusa qualquer desvio.
 */
export type SyncRecordLike = BaseRecord & Record<string, unknown>;

/** `poisoned` e estado LOCAL: sai da fila de envio mas nunca viaja nem se apaga. */
export interface StoredOutboxItemLike extends OutboxItem {
  poisoned?: boolean;
  poisonedAt?: string;
  poisonReason?: string;
}

export interface OutboxRetryUpdateLike {
  id: string;
  attempts: number;
  lastError: string;
  nextAttemptAt: string;
}

export interface OutboxStoreLike {
  take(limit: number, now: string): Promise<StoredOutboxItemLike[]>;
  ack(ids: string[]): Promise<void>;
  reschedule(updates: OutboxRetryUpdateLike[]): Promise<void>;
  poison(ids: string[], reason: string): Promise<void>;
  revive(id: string): Promise<void>;
  enqueue(item: StoredOutboxItemLike): Promise<void>;
  countPending(): Promise<number>;
  listPoisoned(): Promise<StoredOutboxItemLike[]>;
}

export interface RecordStoreLike {
  get(entity: string, id: string): Promise<SyncRecordLike | null>;
  applyRemote(entity: string, record: SyncRecordLike): Promise<void>;
  markSyncState(entity: string, id: string, state: SyncState): Promise<void>;
}

export interface CursorStoreLike {
  get(): Promise<string | undefined>;
  set(cursor: string): Promise<void>;
}

export interface StoredConflictLike extends ConflictReport {
  id: string;
  detectedAt: string;
  serverVersion: number;
}

export interface ConflictStoreLike {
  put(conflict: StoredConflictLike): Promise<void>;
  get(id: string): Promise<StoredConflictLike | null>;
  list(): Promise<StoredConflictLike[]>;
  remove(id: string): Promise<void>;
  count(): Promise<number>;
}

/** Base da fusao a tres vias. Sem ela, toda a divergencia vai a revisao humana. */
export interface SnapshotProviderLike {
  snapshot(
    entity: string,
    recordId: string,
    version: number,
  ): Promise<Record<string, unknown> | null>;
}

export interface SyncRuntimeLike {
  isOnline(): boolean;
  on(event: 'online' | 'offline' | 'visible', handler: () => void): () => void;
}

/** Quem esta a sincronizar, e a partir de que aparelho. */
export interface SyncSessionLike {
  familyId: string;
  memberId: string;
  deviceId: string;
}

export interface SyncEngineDeps {
  transport: SyncTransport;
  outbox: OutboxStoreLike;
  records: RecordStoreLike;
  cursor: CursorStoreLike;
  conflicts: ConflictStoreLike;
  events: EventBusLike;
  /** `null` trava o ciclo sem erro nenhum: e assim que a sync fica dormente. */
  session: () => SyncSessionLike | null;
  logger?: Logger;
  snapshots?: SnapshotProviderLike;
  runtime?: SyncRuntimeLike;
  intervalMs?: number;
}

/** Configuracao do transporte. O URL e por familia, nunca uma constante do codigo. */
export interface TransportConfigLike {
  url: string;
  /** Lido a cada pedido. `null` quando nao ha sessao valida — nunca salvo. */
  getToken: () => Promise<string | null>;
}

export interface SyncModulePort {
  SyncEngine: new (options: SyncEngineDeps) => SyncEngineLike;
  AppsScriptTransport?: new (config: TransportConfigLike) => SyncTransport;
  browserRuntime?: () => SyncRuntimeLike;
}

// --- Intencoes: como um plugin recebe uma acao confirmada -------------------

/**
 * Contrato entre a casca e um plugin para tratar uma intencao da IA.
 *
 * A casca NAO conhece dominios (regra 1) e os plugins nao se importam uns aos
 * outros (regra 8). Por isso o despacho e feito por servico publicado: um plugin
 * chama `ctx.provide('<id-do-plugin>.intents', handler)` no `register()`, e a
 * casca consome-o por id quando a pessoa confirma a acao.
 *
 * Quem nao publicar o servico continua a receber o evento
 * `CoreEvents.AI_ACTION_CONFIRMED` — e a via alternativa, sem valor de retorno.
 */
export interface PluginIntentHandler {
  handle(action: AiAction): Promise<IntentOutcome> | IntentOutcome;
}

export interface IntentOutcome {
  /** Frase em portugues a dizer o que ficou feito. Mostrada tal e qual. */
  message: string;
  /** Registros que sustentam a resposta. Obrigatorio quando ha numeros (ADR-0003). */
  citations?: Array<{ entity: string; recordId: string; label: string }>;
  /** Para onde levar quem quiser ver o resultado. */
  route?: string;
  ok?: boolean;
}

export function intentServiceId(pluginId: string): string {
  return `${pluginId}.intents`;
}

/** Estado de leitura-apenas, com o motivo ja escrito em portugues. */
export interface ReadOnlyState {
  reason: 'sessao-expirada-offline' | 'sem-permissao-de-escrita' | 'plugin-indisponivel';
  title: string;
  message: string;
}

export type { Scope };
